import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  Avatar,
  ButtonGroup,
  Fab,
  Collapse,
  useTheme,
  alpha,
  TablePagination,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Visibility,
  Print,
  FolderOpen,
  Search,
  FilterList,
  Add,
  Edit,
  MoreVert,
  Person,
  Business,
  Assignment,
  Schedule,
  Euro,
  CheckCircle,
  Cancel,
  Warning,
  Download,
  Share,
  Refresh,
  Email,
  EmailRounded,
} from "@mui/icons-material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ReceiptIcon from "@mui/icons-material/Receipt";
import Barcode from "react-barcode";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { getToken, base_url } from "../config/config";
import axios from "axios";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const AnfrageList = () => {
  const theme = useTheme();
  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Dialog states
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openBarcodeDialog, setOpenBarcodeDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [openExcelUploadDialog, setOpenExcelUploadDialog] = useState(false);
  const [openColumnMappingDialog, setOpenColumnMappingDialog] = useState(false);

  // Selected Anfrage for dialogs
  const [selectedAnfrage, setSelectedAnfrage] = useState(null);
  const [selectedBarcodeAnfrage, setSelectedBarcodeAnfrage] = useState(null);
  const [selectedQRAnfrage, setSelectedQRAnfrage] = useState(null);
  const [emailStatus, setEmailStatus] = useState("default");

  // Excel upload states
  const [excelData, setExcelData] = useState([]);
  const [excelColumns, setExcelColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [sequenceNumberMapping, setSequenceNumberMapping] = useState({});

  // Update data state
  const [updateData, setUpdateData] = useState({
    estimatedTime: "",
    estimatedPrice: "",
    EstimatedMaterialCost: "",
    MaxLohnaufwand: "",
    Menge: "",
    Baugruppenzeichnung: "",
    Erläuterung: "",
    isComplete: true,
    angebotErstellt: false,
    // New fields
    anfrageEingang: "",
    anfrage: "",
    kalkulation: "",
    angebotNr: "",
    angebotErstelltDate: "",
    angebotGeschickt: "",
    firma: "",
    knr: "",
    angebotAngenommen: false,
    agNr: "",
  });

  const navigate = useNavigate();
  const token = getToken();

  useEffect(() => {
    fetchAnfragen();
  }, []);

  const fetchAnfragen = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllAnfrag`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setAnfragen(data.anfrag || []);
      setLoading(false);
    } catch (err) {
      console.error("Fehler beim Laden der Daten:", err);
      setError(
        "Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut."
      );
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredAnfragen = anfragen.filter((anfrage) => {
    const matchesSearch =
      anfrage.ANF?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      anfrage.Auftragsbezeichnung?.toLowerCase().includes(
        searchTerm.toLowerCase()
      ) ||
      anfrage.Kunden?.bezeichnung
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && anfrage.workStatus) ||
      (statusFilter === "inactive" && !anfrage.workStatus) ||
      (statusFilter === "complete" && anfrage.isComplete) ||
      (statusFilter === "incomplete" && !anfrage.isComplete);

    return matchesSearch && matchesStatus;
  });

  const paginatedAnfragen = filteredAnfragen.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Status helpers
  const getStatusColor = (status) => {
    if (status) return "success";
    return "error";
  };

  const getCompletenessColor = (isComplete) => {
    if (isComplete) return "success";
    return "warning";
  };

  const toggleRowExpansion = (anfrageId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(anfrageId)) {
      newExpanded.delete(anfrageId);
    } else {
      newExpanded.add(anfrageId);
    }
    setExpandedRows(newExpanded);
  };

  // -----------------------------------------
  //            Delete Anfrage
  // -----------------------------------------
  const handleDeleteAnfrage = async (anfrageId) => {
    try {
      const response = await fetch(
        `${base_url}/v1/deleteAnfrag?id=${anfrageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          method: "DELETE",
        }
      );
      if (response.ok) {
        fetchAnfragen();
      } else {
        throw new Error("Löschen fehlgeschlagen");
      }
    } catch (err) {
      console.error("Fehler beim Löschen der Anfrage:", err);
      setError("Löschen fehlgeschlagen. Bitte versuchen Sie es erneut.");
    }
  };

  // -----------------------------------------
  //           Toggle workStatus
  // -----------------------------------------
  const handleToggleWorkStatus = async (anfrage) => {
    try {
      const newStatus = !anfrage.workStatus;
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const response = await fetch(
        `${base_url}/v1/updateAnfrag?id=${anfrage._id}`,
        {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify({ workStatus: newStatus }),
        }
      );

      if (response.ok) {
        fetchAnfragen();
      } else {
        setError(
          "Aktualisierung des workStatus fehlgeschlagen. Bitte versuchen Sie es erneut."
        );
      }
    } catch (err) {
      console.error("Fehler bei der Aktualisierung des workStatus:", err);
      setError("Aktualisierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    }
  };

  // -----------------------------------------
  //         Update dialog handlers
  // -----------------------------------------
  const handleOpenUpdateDialog = (anfrage) => {
    setSelectedAnfrage(anfrage);
    setUpdateData({
      estimatedTime: anfrage.estimatedTime || "",
      estimatedPrice: anfrage.estimatedPrice || "",
      EstimatedMaterialCost: anfrage.EstimatedMaterialCost || "",
      MaxLohnaufwand: anfrage.MaxLohnaufwand || "",
      Menge: anfrage.Menge || "",
      Baugruppenzeichnung: anfrage.Baugruppenzeichnung || "",
      Erläuterung: anfrage.Erläuterung || "",
      isComplete: anfrage.isComplete !== undefined ? anfrage.isComplete : true,
      angebotErstellt: anfrage.angebotErstellt || false,
      // New fields
      anfrageEingang: anfrage.anfrageEingang || "",
      anfrage: anfrage.anfrage || "",
      kalkulation: anfrage.kalkulation || "",
      angebotNr: anfrage.angebotNr || "",
      angebotErstelltDate: anfrage.angebotErstelltDate || "",
      angebotGeschickt: anfrage.angebotGeschickt || "",
      firma: anfrage.firma || "",
      knr: anfrage.knr || "",
      angebotAngenommen: anfrage.angebotAngenommen || false,
      agNr: anfrage.agNr || "",
    });
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedAnfrage(null);
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = async () => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const response = await fetch(
        `${base_url}/v1/updateAnfrag?id=${selectedAnfrage._id}`,
        {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify(updateData),
        }
      );

      if (response.ok) {
        fetchAnfragen();
        handleCloseUpdateDialog();
      } else {
        setError(
          "Aktualisierung fehlgeschlagen. Bitte versuchen Sie es erneut."
        );
      }
    } catch (err) {
      console.error("Fehler bei der Aktualisierung:", err);
      setError("Aktualisierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    }
  };

  // -----------------------------------------
  //       Convert Anfrage to Project
  // -----------------------------------------
  const handleConvertToProject = async (anfrageId) => {
    try {
      const response = await fetch(
        `${base_url}/v1/convertAnfrageToProject/${anfrageId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          method: "POST",
        }
      );

      if (response.ok) {
        await response.json();
        alert("Erfolgreich in ein Projekt umgewandelt!");
      } else {
        throw new Error("Umwandlung fehlgeschlagen");
      }
    } catch (err) {
      console.error("Fehler bei der Umwandlung in ein Projekt:", err);
      setError(
        "Umwandlung in ein Projekt fehlgeschlagen. Bitte versuchen Sie es erneut."
      );
    }
  };

  // -----------------------------------------
  //            Details Dialog
  // -----------------------------------------
  const handleViewDetails = (anfrage) => {
    setSelectedAnfrage(anfrage);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedAnfrage(null);
  };

  // -----------------------------------------
  //           Barcode Dialog
  // -----------------------------------------
  const handleOpenBarcodeDialog = (anfrage) => {
    setSelectedBarcodeAnfrage(anfrage);
    setOpenBarcodeDialog(true);
  };

  const handleCloseBarcodeDialog = () => {
    setOpenBarcodeDialog(false);
    setSelectedBarcodeAnfrage(null);
  };

  const handlePrintBarcode = () => {
    const printContent = document.getElementById("barcode-print");
    const WinPrint = window.open("", "", "width=600,height=600");
    WinPrint.document.write(
      "<html><head><title>Barcode drucken</title></head><body>"
    );
    WinPrint.document.write(printContent.innerHTML);
    WinPrint.document.write("</body></html>");
    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
    WinPrint.close();
  };

  // -----------------------------------------
  //           QR Code Dialog
  // -----------------------------------------
  const handleOpenQRDialog = (anfrage) => {
    setSelectedQRAnfrage(anfrage);
    setOpenQRDialog(true);
  };

  const handleCloseQRDialog = () => {
    setOpenQRDialog(false);
    setSelectedQRAnfrage(null);
  };

  const handlePrintQRCode = () => {
    const printContent = document.getElementById("qrcode-print");
    const WinPrint = window.open("", "", "width=600,height=600");
    WinPrint.document.write(
      "<html><head><title>QR-Code drucken</title></head><body>"
    );
    WinPrint.document.write(printContent.innerHTML);
    WinPrint.document.write("</body></html>");
    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
    WinPrint.close();
  };

  // -----------------------------------------
  //  Navigate to Document Uploader
  // -----------------------------------------
  const handleUploadDocuments = (anfrage) => {
    navigate(`/anfrageUploader/${anfrage.ANF}`);
  };

  // -----------------------------------------
  //  Navigate to Invoice Uploader
  // -----------------------------------------
  const handleUploadInvoice = (anfrage) => {
    navigate(`/anfrageInvoiceUpload/${anfrage.ANF}`);
  };

  // -----------------------------------------
  //  Navigate to File Manager
  // -----------------------------------------
  const handleViewFileManager = (anfrage) => {
    navigate(`/anfrageFileManager/${anfrage.ANF}`);
  };

  const handleSendEmail = async (anfrage) => {
    try {
      const response = await axios.post(
        `${base_url}/v1/sendEmailRemainder`,

        { data: anfrage },

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        setEmailStatus("success");
      } else {
        setEmailStatus("error");
      }
    } catch (err) {
      console.error("Error sending email:", err);
      setEmailStatus("error");
    }
  };

  // pick the color based on status
  const iconColor =
    emailStatus === "success"
      ? theme.palette.success.main
      : emailStatus === "error"
      ? theme.palette.error.main
      : theme.palette.primary.main;

  // -----------------------------------------
  //          Excel Upload Functions
  // -----------------------------------------
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          alert('Excel file must have at least 2 rows (header + data)');
          return;
        }
        
        const headers = jsonData[0];
        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
        
        setExcelColumns(headers);
        setExcelData(rows);
        setOpenExcelUploadDialog(false);
        setOpenColumnMappingDialog(true);
        
        // Initialize column mapping
        const initialMapping = {};
        headers.forEach((header, index) => {
          const normalizedHeader = header?.toString().toLowerCase().trim();
          // Auto-map common columns
          if (normalizedHeader.includes('anf') || normalizedHeader.includes('nummer')) {
            initialMapping[index] = 'ANF';
          } else if (normalizedHeader.includes('kunde') || normalizedHeader.includes('customer')) {
            initialMapping[index] = 'Kunden';
          } else if (normalizedHeader.includes('bezeichnung') || normalizedHeader.includes('description')) {
            initialMapping[index] = 'Auftragsbezeichnung';
          } else if (normalizedHeader.includes('menge') || normalizedHeader.includes('quantity')) {
            initialMapping[index] = 'Menge';
          } else if (normalizedHeader.includes('preis') || normalizedHeader.includes('price')) {
            initialMapping[index] = 'estimatedPrice';
          } else if (normalizedHeader.includes('zeit') || normalizedHeader.includes('time')) {
            initialMapping[index] = 'estimatedTime';
          } else if (normalizedHeader.includes('material')) {
            initialMapping[index] = 'EstimatedMaterialCost';
          } else if (normalizedHeader.includes('lohn')) {
            initialMapping[index] = 'MaxLohnaufwand';
          } else if (normalizedHeader.includes('erläuterung') || normalizedHeader.includes('notes')) {
            initialMapping[index] = 'Erläuterung';
          }
        });
        
        setColumnMapping(initialMapping);
      } catch (error) {
        console.error('Error reading Excel file:', error);
        alert('Error reading Excel file. Please make sure it\'s a valid Excel file.');
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  const handleColumnMappingChange = (columnIndex, mappedField) => {
    setColumnMapping(prev => ({
      ...prev,
      [columnIndex]: mappedField
    }));
  };

  const handleSequenceNumberChange = (rowIndex, sequenceNumber) => {
    setSequenceNumberMapping(prev => ({
      ...prev,
      [rowIndex]: sequenceNumber
    }));
  };

  const processExcelData = async () => {
    try {
      setLoading(true);
      const processedData = [];
      
      // Get current year for ANF generation
      const currentYear = new Date().getFullYear().toString().slice(-2);
      
      for (let i = 0; i < excelData.length; i++) {
        const row = excelData[i];
        const anfrageData = {};
        
        // Map columns to fields
        Object.entries(columnMapping).forEach(([columnIndex, fieldName]) => {
          if (fieldName && row[columnIndex] !== undefined) {
            anfrageData[fieldName] = row[columnIndex];
          }
        });
        
        // Generate ANF number - either user provided or auto-generated
        if (!anfrageData.ANF) {
          const userSequence = sequenceNumberMapping[i];
          if (userSequence) {
            anfrageData.ANF = `${userSequence}-00-00-${currentYear}`;
          } else {
            // Auto-generate sequence number
            const baseSequence = 1000 + i;
            anfrageData.ANF = `${baseSequence}-00-00-${currentYear}`;
          }
        }
        
        // Set default values for required fields
        anfrageData.estimatedTime = anfrageData.estimatedTime || "0";
        anfrageData.estimatedPrice = anfrageData.estimatedPrice || "0";
        anfrageData.Menge = anfrageData.Menge || "1";
        anfrageData.isComplete = false;
        
        processedData.push(anfrageData);
      }
      
      // Send to backend
      const response = await fetch(`${base_url}/v1/createBulkAnfrag`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ anfragen: processedData }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Bulk upload successful:', result);
        alert(`Successfully uploaded ${processedData.length} Anfragen!`);
        fetchAnfragen(); // Refresh the list
        setOpenColumnMappingDialog(false);
        resetExcelUploadState();
      } else {
        throw new Error('Failed to upload Anfragen');
      }
    } catch (error) {
      console.error('Error processing Excel data:', error);
      alert('Error uploading Anfragen. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetExcelUploadState = () => {
    setExcelData([]);
    setExcelColumns([]);
    setColumnMapping({});
    setUploadedFile(null);
    setSequenceNumberMapping({});
  };

  // -----------------------------------------
  //          Excel Export Function
  // -----------------------------------------
  const handleExportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredAnfragen.map(anfrage => ({
        'ANF': anfrage.ANF,
        'Kunde': anfrage.Kunden?.bezeichnung || '',
        'Auftragsbezeichnung': anfrage.Auftragsbezeichnung || '',
        'Menge': anfrage.Menge || '',
        'Geschätzter Preis': anfrage.estimatedPrice || '',
        'Geschätzte Zeit': anfrage.estimatedTime || '',
        'Materialkosten': anfrage.EstimatedMaterialCost || '',
        'Max Lohnaufwand': anfrage.MaxLohnaufwand || '',
        'Baugruppenzeichnung': anfrage.Baugruppenzeichnung || '',
        'Erläuterung': anfrage.Erläuterung || '',
        'Status': anfrage.workStatus ? 'Aktiv' : 'Inaktiv',
        'Vollständig': anfrage.isComplete ? 'Ja' : 'Nein',
        'Angebot erstellt': anfrage.angebotErstellt ? 'Ja' : 'Nein',
        'Mitarbeiter': anfrage.assignedEmployee?.name || 'Nicht zugewiesen'
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Anfragen");

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      ws['!cols'] = colWidths;

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Download file
      const fileName = `Anfragen_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Available field options for mapping
  const availableFields = [
    { value: '', label: 'Nicht zuordnen' },
    { value: 'ANF', label: 'ANF Nummer' },
    { value: 'Kunden', label: 'Kunde' },
    { value: 'Auftragsbezeichnung', label: 'Auftragsbezeichnung' },
    { value: 'Menge', label: 'Menge' },
    { value: 'estimatedPrice', label: 'Geschätzter Preis' },
    { value: 'estimatedTime', label: 'Geschätzte Zeit' },
    { value: 'EstimatedMaterialCost', label: 'Materialkosten' },
    { value: 'MaxLohnaufwand', label: 'Max Lohnaufwand' },
    { value: 'Baugruppenzeichnung', label: 'Baugruppenzeichnung' },
    { value: 'Erläuterung', label: 'Erläuterung' },
    { value: 'Artikelnummer', label: 'Artikelnummer' },
    { value: 'anfrageEingang', label: 'Anfrage Eingang' },
    { value: 'anfrage', label: 'Anfrage' },
    { value: 'kalkulation', label: 'Kalkulation' },
    { value: 'angebotNr', label: 'Angebot Nr.' },
    { value: 'angebotErstelltDate', label: 'Angebot erstellt (Datum)' },
    { value: 'angebotGeschickt', label: 'Angebot geschickt' },
    { value: 'firma', label: 'Firma' },
    { value: 'knr', label: 'Knr.' },
    { value: 'angebotAngenommen', label: 'Angebot angenommen' },
    { value: 'agNr', label: 'AG Nr.' }
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" color="textSecondary">
          Lade Anfragen...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              gutterBottom
            >
              Anfragenverwaltung
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Verwalten Sie alle Ihre Anfragen an einem zentralen Ort
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Chip
              label={`${filteredAnfragen.length} Anfragen`}
              sx={{
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                color: "white",
                fontWeight: "bold",
              }}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => navigate('/AnfrageForm')}
              sx={{
                backgroundColor: theme.palette.common.white,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.9),
                },
              }}
            >
              Neue Anfrage
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenExcelUploadDialog(true)}
              sx={{
                backgroundColor: theme.palette.common.white,
                color: theme.palette.info.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.9),
                },
              }}
            >
              Excel Upload
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Download />}
              onClick={handleExportToExcel}
              sx={{
                backgroundColor: theme.palette.common.white,
                color: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.9),
                },
              }}
            >
              Excel Export
            </Button>
          </Box>
        </Box>

        {/* Search and Filter Bar */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Nach ANF, Bezeichnung oder Kunde suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "rgba(255,255,255,0.7)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: alpha(theme.palette.common.white, 0.15),
                  color: "white",
                  "& fieldset": {
                    borderColor: alpha(theme.palette.common.white, 0.3),
                  },
                  "&:hover fieldset": {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.common.white,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.7)",
                  opacity: 1,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: alpha(theme.palette.common.white, 0.15),
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.common.white, 0.3),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.common.white,
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="active">Aktiv</MenuItem>
                <MenuItem value="inactive">Inaktiv</MenuItem>
                <MenuItem value="complete">Vollständig</MenuItem>
                <MenuItem value="incomplete">Unvollständig</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAnfragen}
              sx={{
                borderColor: alpha(theme.palette.common.white, 0.3),
                color: "white",
                "&:hover": {
                  borderColor: theme.palette.common.white,
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                },
              }}
            >
              Aktualisieren
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setError("")}
            >
              ×
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <Assignment />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {anfragen.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Gesamt Anfragen
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {anfragen.filter((a) => a.workStatus).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Aktive Anfragen
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {anfragen.filter((a) => a.isComplete).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Vollständig
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                  <Euro />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {anfragen.filter((a) => a.angebotErstellt).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Angebote erstellt
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: "bold", py: 2 }}>ANF</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Kunde</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Auftragsbezeichnung
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Mitarbeiter</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Menge</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Preis</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Vollständigkeit
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Angebot</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Angebot Nr.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Firma</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>AG Nr.</TableCell>
                <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAnfragen.map((anfrage) => (
                <React.Fragment key={anfrage._id}>
                  <TableRow
                    hover
                    sx={{
                      borderBottom: expandedRows.has(anfrage._id)
                        ? "none"
                        : undefined,
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                        >
                          {anfrage.ANF}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {anfrage.Kunden?.bezeichnung || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {anfrage.Kunden?.konto || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {anfrage.Auftragsbezeichnung}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">
                          {anfrage.assignedEmployee?.name || "Nicht zugewiesen"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{anfrage.Menge}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {anfrage.estimatedPrice
                          ? `€${anfrage.estimatedPrice}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={anfrage.workStatus ? "Aktiv" : "Inaktiv"}
                        color={getStatusColor(anfrage.workStatus)}
                        onClick={() => handleToggleWorkStatus(anfrage)}
                        sx={{ cursor: "pointer", minWidth: 80 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          anfrage.isComplete ? "Vollständig" : "Unvollständig"
                        }
                        color={getCompletenessColor(anfrage.isComplete)}
                        onClick={() => handleOpenUpdateDialog(anfrage)}
                        sx={{ cursor: "pointer", minWidth: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={anfrage.angebotErstellt ? "Ja" : "Nein"}
                        color={anfrage.angebotErstellt ? "success" : "default"}
                        onClick={() => handleOpenUpdateDialog(anfrage)}
                        sx={{ cursor: "pointer", minWidth: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {anfrage.angebotNr || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {anfrage.firma || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {anfrage.agNr || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Send Email">
                          <IconButton
                            size="small"
                            onClick={() => handleSendEmail(anfrage)}
                            sx={{ color: iconColor }}
                          >
                            <EmailRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Details anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(anfrage)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bearbeiten">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenUpdateDialog(anfrage)}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mehr Aktionen">
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(anfrage._id)}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Actions Row */}
                  <TableRow>
                    <TableCell
                      colSpan={13}
                      sx={{
                        py: 0,
                        borderBottom: expandedRows.has(anfrage._id)
                          ? `1px solid ${theme.palette.divider}`
                          : "none",
                      }}
                    >
                      <Collapse
                        in={expandedRows.has(anfrage._id)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.02
                            ),
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Erweiterte Aktionen
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Button
                              size="small"
                              startIcon={<FolderOpen />}
                              onClick={() => handleViewFileManager(anfrage)}
                              variant="outlined"
                            >
                              Dateien
                            </Button>
                            <Button
                              size="small"
                              startIcon={<CompareArrowsIcon />}
                              onClick={() =>
                                handleConvertToProject(anfrage._id)
                              }
                              variant="outlined"
                            >
                              Zu Projekt
                            </Button>
                            <Button
                              size="small"
                              startIcon={<Print />}
                              onClick={() => handleOpenBarcodeDialog(anfrage)}
                              variant="outlined"
                            >
                              Barcode
                            </Button>
                            <Button
                              size="small"
                              startIcon={<QrCodeIcon />}
                              onClick={() => handleOpenQRDialog(anfrage)}
                              variant="outlined"
                            >
                              QR-Code
                            </Button>
                            <Button
                              size="small"
                              startIcon={<UploadFileIcon />}
                              onClick={() => handleUploadDocuments(anfrage)}
                              variant="outlined"
                            >
                              Dokumente
                            </Button>
                            <Button
                              size="small"
                              startIcon={<ReceiptIcon />}
                              onClick={() => handleUploadInvoice(anfrage)}
                              variant="outlined"
                            >
                              Rechnung
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteAnfrage(anfrage._id)}
                              color="error"
                              variant="outlined"
                            >
                              Löschen
                            </Button>
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredAnfragen.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} von ${count}`
          }
        />
      </Paper>

      {/* Enhanced Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <Assignment />
            </Avatar>
            <Box>
              <Typography variant="h6">Anfragedetails</Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedAnfrage?.ANF}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedAnfrage && (
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Grundinformationen
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        ANF-Nummer
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedAnfrage.ANF}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Lieferant
                      </Typography>
                      <Typography variant="body1">
                        {selectedAnfrage.lieferantID?.bezeichnung || "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Auftragsbezeichnung
                      </Typography>
                      <Typography variant="body1">
                        {selectedAnfrage.Auftragsbezeichnung}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Menge
                      </Typography>
                      <Typography variant="body1">
                        {selectedAnfrage.Menge}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Finanzielle Details
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Geschätzter Preis
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedAnfrage.estimatedPrice
                          ? `€${selectedAnfrage.estimatedPrice}`
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Materialkosten
                      </Typography>
                      <Typography variant="body1">
                        {selectedAnfrage.EstimatedMaterialCost
                          ? `€${selectedAnfrage.EstimatedMaterialCost}`
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Max Lohnaufwand
                      </Typography>
                      <Typography variant="body1">
                        {selectedAnfrage.MaxLohnaufwand
                          ? `€${selectedAnfrage.MaxLohnaufwand}`
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Geschätzte Zeit
                      </Typography>
                      <Typography variant="body1">
                        {selectedAnfrage.estimatedTime || "N/A"}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Status & Zuordnung
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="textSecondary">
                          Arbeitsstatus
                        </Typography>
                        <Chip
                          label={
                            selectedAnfrage.workStatus ? "Aktiv" : "Inaktiv"
                          }
                          color={getStatusColor(selectedAnfrage.workStatus)}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="textSecondary">
                          Vollständigkeit
                        </Typography>
                        <Chip
                          label={
                            selectedAnfrage.isComplete
                              ? "Vollständig"
                              : "Unvollständig"
                          }
                          color={getCompletenessColor(
                            selectedAnfrage.isComplete
                          )}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="textSecondary">
                          Angebot erstellt
                        </Typography>
                        <Chip
                          label={
                            selectedAnfrage.angebotErstellt ? "Ja" : "Nein"
                          }
                          color={
                            selectedAnfrage.angebotErstellt
                              ? "success"
                              : "default"
                          }
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="textSecondary">
                          Mitarbeiter
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          {selectedAnfrage.assignedEmployee?.name ||
                            "Nicht zugewiesen"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {(selectedAnfrage.Baugruppenzeichnung ||
                selectedAnfrage.Erläuterung) && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Zusätzliche Informationen
                    </Typography>
                    {selectedAnfrage.Baugruppenzeichnung && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          Baugruppenzeichnung
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.Baugruppenzeichnung}
                        </Typography>
                      </Box>
                    )}
                    {selectedAnfrage.Erläuterung && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Erläuterung
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.Erläuterung}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>
              )}

              {/* New Anfrage Information Section */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Angebotsinformationen
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Anfrage Eingang
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.anfrageEingang ? new Date(selectedAnfrage.anfrageEingang).toLocaleDateString() : "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Angebot Nr.
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.angebotNr || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Firma
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.firma || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Kalkulation
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.kalkulation || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          AG Nr.
                        </Typography>
                        <Typography variant="body1">
                          {selectedAnfrage.agNr || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Angebot angenommen
                        </Typography>
                        <Chip
                          label={selectedAnfrage.angebotAngenommen ? "Ja" : "Nein"}
                          color={selectedAnfrage.angebotAngenommen ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <ButtonGroup variant="contained">
            <Button
              onClick={() => {
                handleConvertToProject(selectedAnfrage._id);
                handleCloseDetailsDialog();
              }}
              startIcon={<CompareArrowsIcon />}
            >
              Zu Projekt
            </Button>
            <Button
              onClick={() => {
                handleViewFileManager(selectedAnfrage);
                handleCloseDetailsDialog();
              }}
              startIcon={<FolderOpen />}
            >
              Dateien
            </Button>
          </ButtonGroup>
          <Button onClick={handleCloseDetailsDialog} color="inherit">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Update Dialog */}
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.info.main }}>
              <Edit />
            </Avatar>
            <Typography variant="h6">Anfrage aktualisieren</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Geschätzte Zeit"
                name="estimatedTime"
                type="number"
                value={updateData.estimatedTime}
                onChange={handleUpdateChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">h</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Geschätzter Preis"
                name="estimatedPrice"
                type="number"
                value={updateData.estimatedPrice}
                onChange={handleUpdateChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">€</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Geschätzte Materialkosten"
                name="EstimatedMaterialCost"
                type="number"
                value={updateData.EstimatedMaterialCost}
                onChange={handleUpdateChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">€</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Lohnaufwand"
                name="MaxLohnaufwand"
                type="number"
                value={updateData.MaxLohnaufwand}
                onChange={handleUpdateChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">€</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Menge"
                name="Menge"
                type="number"
                value={updateData.Menge}
                onChange={handleUpdateChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Baugruppenzeichnung"
                name="Baugruppenzeichnung"
                value={updateData.Baugruppenzeichnung}
                onChange={handleUpdateChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Erläuterung"
                name="Erläuterung"
                value={updateData.Erläuterung}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            {/* New fields section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Angebotsinformationen
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Anfrage Eingang"
                name="anfrageEingang"
                type="date"
                value={updateData.anfrageEingang}
                onChange={handleUpdateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Anfrage"
                name="anfrage"
                value={updateData.anfrage}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kalkulation"
                name="kalkulation"
                value={updateData.kalkulation}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Angebot - Nr."
                name="angebotNr"
                value={updateData.angebotNr}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Angebot erstellt (Datum)"
                name="angebotErstelltDate"
                type="date"
                value={updateData.angebotErstelltDate}
                onChange={handleUpdateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Angebot geschickt"
                name="angebotGeschickt"
                type="date"
                value={updateData.angebotGeschickt}
                onChange={handleUpdateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Firma"
                name="firma"
                value={updateData.firma}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Knr."
                name="knr"
                value={updateData.knr}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="AG - Nr."
                name="agNr"
                value={updateData.agNr}
                onChange={handleUpdateChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Status Optionen
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateData.isComplete}
                        onChange={(e) =>
                          setUpdateData((prev) => ({
                            ...prev,
                            isComplete: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Anfrage ist vollständig"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateData.angebotErstellt}
                        onChange={(e) =>
                          setUpdateData((prev) => ({
                            ...prev,
                            angebotErstellt: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Angebot erstellt"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateData.angebotAngenommen}
                        onChange={(e) =>
                          setUpdateData((prev) => ({
                            ...prev,
                            angebotAngenommen: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Angebot angenommen"
                  />
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseUpdateDialog} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<Edit />}
          >
            Aktualisieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Barcode Dialog */}
      <Dialog
        open={openBarcodeDialog}
        onClose={handleCloseBarcodeDialog}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
              <Print />
            </Avatar>
            <Typography variant="h6">Barcode drucken</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedBarcodeAnfrage && (
            <Box
              id="barcode-print"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                ANF: {selectedBarcodeAnfrage.ANF}
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: `2px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Barcode value={selectedBarcodeAnfrage.ANF} />
              </Box>
              <Typography variant="body2" color="textSecondary">
                {selectedBarcodeAnfrage.Auftragsbezeichnung}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseBarcodeDialog} color="inherit">
            Schließen
          </Button>
          <Button
            onClick={handlePrintBarcode}
            variant="contained"
            startIcon={<Print />}
          >
            Drucken
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced QR Code Dialog */}
      <Dialog
        open={openQRDialog}
        onClose={handleCloseQRDialog}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
              <QrCodeIcon />
            </Avatar>
            <Typography variant="h6">QR-Code für Rechnung</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedQRAnfrage && (
            <Box
              id="qrcode-print"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                ANF: {selectedQRAnfrage.ANF}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, maxWidth: 300 }}>
                Scannen Sie diesen QR-Code, um eine Rechnung für diese Anfrage
                hochzuladen
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: `2px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <QRCodeCanvas
                  value={`http://cnc-heidenau.de/anfrageInvoiceUpload/${selectedQRAnfrage.ANF}`}
                  size={256}
                  level="H"
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                {selectedQRAnfrage.Auftragsbezeichnung}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseQRDialog} color="inherit">
            Schließen
          </Button>
          <Button
            onClick={handlePrintQRCode}
            variant="contained"
            startIcon={<Print />}
          >
            Drucken
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Upload Dialog */}
      <Dialog
        open={openExcelUploadDialog}
        onClose={() => setOpenExcelUploadDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.info.main }}>
              <UploadFileIcon />
            </Avatar>
            <Typography variant="h6">Excel File Upload</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={3}>
            <Typography variant="body2" color="textSecondary">
              Upload an Excel file to create multiple Anfragen at once. The file should contain a header row with column names.
            </Typography>
            
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              fullWidth
              sx={{ py: 2 }}
            >
              Choose Excel File
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
              />
            </Button>
            
            {uploadedFile && (
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Selected file: {uploadedFile.name}
                </Typography>
              </Box>
            )}
            
            <Typography variant="body2" color="textSecondary">
              <strong>Tip:</strong> Download the template Excel file from the system to see the expected format.
            </Typography>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenExcelUploadDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            href="/templates/anfrage.xlsx"
            download="anfrage_template.xlsx"
            variant="outlined"
            startIcon={<Download />}
          >
            Download Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog
        open={openColumnMappingDialog}
        onClose={() => setOpenColumnMappingDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
              <CompareArrowsIcon />
            </Avatar>
            <Typography variant="h6">Column Mapping</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={3}>
            <Typography variant="body2" color="textSecondary">
              Map the Excel columns to the corresponding Anfrage fields. You can also provide custom sequence numbers for each row.
            </Typography>
            
            {/* Column Mapping Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Excel Column</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Sample Data</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Map to Field</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {excelColumns.map((column, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {column || `Column ${index + 1}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {excelData[0]?.[index] || 'No data'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={columnMapping[index] || ''}
                            onChange={(e) => handleColumnMappingChange(index, e.target.value)}
                            displayEmpty
                          >
                            {availableFields.map((field) => (
                              <MenuItem key={field.value} value={field.value}>
                                {field.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Data Preview with Sequence Number Input */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Data Preview & Sequence Numbers
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Preview the first 5 rows and optionally provide custom sequence numbers. Leave blank for auto-generation.
              </Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Row</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Custom Sequence</TableCell>
                      {Object.values(columnMapping).filter(Boolean).map((field, index) => (
                        <TableCell key={index} sx={{ fontWeight: "bold" }}>
                          {availableFields.find(f => f.value === field)?.label || field}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelData.slice(0, 5).map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell>{rowIndex + 1}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="1000"
                            value={sequenceNumberMapping[rowIndex] || ''}
                            onChange={(e) => handleSequenceNumberChange(rowIndex, e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        {Object.entries(columnMapping).filter(([, field]) => field).map(([columnIndex, field], index) => (
                          <TableCell key={index}>
                            <Typography variant="body2">
                              {row[columnIndex] || '-'}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {excelData.length > 5 && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  ... and {excelData.length - 5} more rows
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setOpenColumnMappingDialog(false);
              resetExcelUploadState();
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={processExcelData}
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={Object.values(columnMapping).filter(Boolean).length === 0}
          >
            Upload {excelData.length} Anfragen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
        onClick={() => navigate('/AnfrageForm')}
      >
        <Add />
      </Fab>
    </Container>
  );
};

export default AnfrageList;
